import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import api from "../services/api";
import "./AverageCogsPage.css";

const emptyPayrollRow = {
  employee_id: "",
  hours: "",
};

const AverageCogsPage = () => {
  const { id: businessId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const queryParams = new URLSearchParams(location.search);
  const accountId = queryParams.get("account_id") || null;
  const accountMask = queryParams.get("mask") || null;
  const accountType = queryParams.get("subtype") || null;

  const [loading, setLoading] = useState(true);
  const [employeesLoading, setEmployeesLoading] = useState(true);
  const [fixedCostsLoading, setFixedCostsLoading] = useState(true);
  const [logsLoading, setLogsLoading] = useState(true);
  const [logSaving, setLogSaving] = useState(false);

  const [logDate, setLogDate] = useState(() => {
    const d = new Date();
    return d.toISOString().slice(0, 10);
  });

  const [netSales, setNetSales] = useState("");

  const [data, setData] = useState({
    months: [],
    totals: {
      income: 0,
      cogs: 0,
    },
    weighted_average_cogs_percentage: 0,
    simple_average_cogs_percentage: 0,
  });

  const [employees, setEmployees] = useState([]);
  const [fixedCosts, setFixedCosts] = useState([]);
  const [logs, setLogs] = useState([]);
  const [payrollRows, setPayrollRows] = useState([{ ...emptyPayrollRow }]);

  const formatMoney = (value) => {
    const num = parseFloat(value || 0);
    const sign = num < 0 ? "-" : "";
    return `${sign}${Math.abs(num).toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
    })}`;
  };

  const toNum = (value) => {
    const num = parseFloat(value);
    return Number.isFinite(num) ? num : 0;
  };

  const getDaysInMonthFromDate = (dateString) => {
    const d = dateString ? new Date(`${dateString}T00:00:00`) : new Date();
    if (Number.isNaN(d.getTime())) {
      const today = new Date();
      return new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
    }
    return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  };

  const fetchAverageCogs = async () => {
    try {
      setLoading(true);

      const params = {};
      if (accountId) {
        params.account_id = accountId;
      }

      const res = await api.get(`/reports/average-cogs/${businessId}`, {
        params,
      });

      setData({
        months: Array.isArray(res.data?.months) ? res.data.months : [],
        totals: res.data?.totals || { income: 0, cogs: 0 },
        weighted_average_cogs_percentage: parseFloat(
          res.data?.weighted_average_cogs_percentage || 0
        ),
        simple_average_cogs_percentage: parseFloat(
          res.data?.simple_average_cogs_percentage || 0
        ),
      });
    } catch (err) {
      console.error("Failed to fetch average COGS data:", err);
      alert("Could not load Average Cost of Goods data.");
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployeePayrates = async () => {
    try {
      setEmployeesLoading(true);
      const res = await api.get(
        `/reports/average-cogs/${businessId}/employees-payrates`
      );
      setEmployees(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to fetch employee pay rates:", err);
      alert("Could not load employee pay rates.");
    } finally {
      setEmployeesLoading(false);
    }
  };

  const fetchFixedCosts = async () => {
    try {
      setFixedCostsLoading(true);
      const res = await api.get(`/fixed-costs/${businessId}`);
      setFixedCosts(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to fetch fixed costs:", err);
      alert("Could not load fixed costs.");
    } finally {
      setFixedCostsLoading(false);
    }
  };

  const fetchLogs = async () => {
    try {
      setLogsLoading(true);
      const res = await api.get(`/reports/average-cogs/${businessId}/daily-log`);
      setLogs(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to fetch daily logs:", err);
      alert("Could not load daily logs.");
    } finally {
      setLogsLoading(false);
    }
  };

  useEffect(() => {
    fetchAverageCogs();
    fetchEmployeePayrates();
    fetchFixedCosts();
    fetchLogs();
    // eslint-disable-next-line
  }, [businessId, accountId]);

  const getEmployeeById = (employeeId) => {
    return employees.find((emp) => String(emp.id) === String(employeeId)) || null;
  };

  const getEmployeeDisplayRate = (employee) => {
    if (!employee) return "—";

    const paymentType = String(employee.payment_type || "").toLowerCase();

    if (paymentType === "hourly") {
      return `${formatMoney(employee.hourly_amt)}/hr`;
    }

    if (paymentType === "salary" || paymentType === "salaried") {
      return `${formatMoney(employee.salary_amt)}/yr (${formatMoney(
        employee.daily_rate
      )}/day)`;
    }

    return "—";
  };

  const calculatePayrollAmount = (row) => {
    const employee = getEmployeeById(row.employee_id);
    if (!employee) return 0;

    const paymentType = String(employee.payment_type || "").toLowerCase();
    const hours = toNum(row.hours);

    if (paymentType === "hourly") {
      return Number(((toNum(employee.hourly_amt) * hours)).toFixed(2));
    }

    if (paymentType === "salary" || paymentType === "salaried") {
      return Number(toNum(employee.daily_rate).toFixed(2));
    }

    return 0;
  };

  const handlePayrollRowChange = (index, field, value) => {
    setPayrollRows((prev) =>
      prev.map((row, i) =>
        i === index
          ? {
              ...row,
              [field]: value,
            }
          : row
      )
    );
  };

  const handleAddPayrollRow = () => {
    setPayrollRows((prev) => [...prev, { ...emptyPayrollRow }]);
  };

  const handleRemovePayrollRow = (index) => {
    setPayrollRows((prev) => prev.filter((_, i) => i !== index));
  };

  const daysInMonth = useMemo(() => {
    return getDaysInMonthFromDate(logDate);
  }, [logDate]);

  const numericNetSales = useMemo(() => toNum(netSales), [netSales]);

  const cogsAmount = useMemo(() => {
    return Number(
      (
        numericNetSales *
        (toNum(data.weighted_average_cogs_percentage) / 100)
      ).toFixed(2)
    );
  }, [numericNetSales, data.weighted_average_cogs_percentage]);

  const payrollSpotCheckTotal = useMemo(() => {
  const total = payrollRows.reduce((sum, row) => {
    const employee =
      employees.find((emp) => String(emp.id) === String(row.employee_id)) || null;

    if (!employee) return sum;

    const paymentType = String(employee.payment_type || "").toLowerCase();
    const hours = toNum(row.hours);

    if (paymentType === "hourly") {
      return sum + Number((toNum(employee.hourly_amt) * hours).toFixed(2));
    }

    if (paymentType === "salary" || paymentType === "salaried") {
      return sum + Number(toNum(employee.daily_rate).toFixed(2));
    }

    return sum;
  }, 0);

  return Number(total.toFixed(2));
}, [payrollRows, employees]);
  

  const payrollTaxAmount = useMemo(() => {
    return Number((payrollSpotCheckTotal * 0.0765).toFixed(2));
  }, [payrollSpotCheckTotal]);

  const fixedAmountRows = useMemo(() => {
    return fixedCosts
      .filter((cost) => cost.is_active === 1 || cost.is_active === true || cost.is_active == null)
      .filter((cost) => cost.cost_type === "amount")
      .map((cost) => {
        const dailyAmount = Number((toNum(cost.amount) / daysInMonth).toFixed(2));
        return {
          id: cost.id,
          cost_name: cost.cost_name,
          cost_type: cost.cost_type,
          monthly_amount: toNum(cost.amount),
          daily_amount: dailyAmount,
        };
      });
  }, [fixedCosts, daysInMonth]);

  const fixedPercentageRows = useMemo(() => {
    return fixedCosts
      .filter((cost) => cost.is_active === 1 || cost.is_active === true || cost.is_active == null)
      .filter((cost) => cost.cost_type === "percentage")
      .map((cost) => {
        const calculatedAmount = Number(
          (numericNetSales * (toNum(cost.percentage) / 100)).toFixed(2)
        );
        return {
          id: cost.id,
          cost_name: cost.cost_name,
          cost_type: cost.cost_type,
          percentage: toNum(cost.percentage),
          calculated_amount: calculatedAmount,
        };
      });
  }, [fixedCosts, numericNetSales]);

  const fixedDailyAmountTotal = useMemo(() => {
    return Number(
      fixedAmountRows.reduce((sum, row) => sum + toNum(row.daily_amount), 0).toFixed(2)
    );
  }, [fixedAmountRows]);

  const fixedPercentageAmountTotal = useMemo(() => {
    return Number(
      fixedPercentageRows
        .reduce((sum, row) => sum + toNum(row.calculated_amount), 0)
        .toFixed(2)
    );
  }, [fixedPercentageRows]);

  const fixedCostTotal = useMemo(() => {
    return Number((fixedDailyAmountTotal + fixedPercentageAmountTotal).toFixed(2));
  }, [fixedDailyAmountTotal, fixedPercentageAmountTotal]);

  const totalProfitLoss = useMemo(() => {
    return Number(
      (
        numericNetSales -
        cogsAmount -
        payrollSpotCheckTotal -
        payrollTaxAmount -
        fixedCostTotal
      ).toFixed(2)
    );
  }, [
    numericNetSales,
    cogsAmount,
    payrollSpotCheckTotal,
    payrollTaxAmount,
    fixedCostTotal,
  ]);

  const handleLogIt = async () => {
    try {
      if (!logDate) {
        alert("Please select a date.");
        return;
      }

      setLogSaving(true);

      const payrollRowsJson = payrollRows.map((row) => {
        const emp = getEmployeeById(row.employee_id);
        return {
          employee_id: row.employee_id || null,
          employee_name: emp?.full_name || "",
          payment_type: emp?.payment_type || "",
          hours: row.hours || "",
          payroll_amount: calculatePayrollAmount(row),
        };
      });

      const fixedCostRowsJson = {
        amount_rows: fixedAmountRows,
        percentage_rows: fixedPercentageRows,
      };

      await api.post(`/reports/average-cogs/${businessId}/daily-log`, {
        log_date: logDate,
        net_sales: numericNetSales,
        avg_cogs_percentage: toNum(data.weighted_average_cogs_percentage),
        cogs_amount: cogsAmount,
        payroll_amount: payrollSpotCheckTotal,
        payroll_tax_amount: payrollTaxAmount,
        fixed_daily_amount_total: fixedDailyAmountTotal,
        fixed_percentage_amount_total: fixedPercentageAmountTotal,
        fixed_cost_total: fixedCostTotal,
        profit_loss_total: totalProfitLoss,
        payroll_rows_json: payrollRowsJson,
        fixed_cost_rows_json: fixedCostRowsJson,
      });

      await fetchLogs();
      alert("Daily profit/loss log saved successfully.");
    } catch (err) {
      console.error("Failed to save daily log:", err);
      alert(err?.response?.data?.message || "Could not save daily log.");
    } finally {
      setLogSaving(false);
    }
  };

  return (
    <div className="payroll-sync-content">
    <div className="avg-cogs-container">
      <h1 className="pw-page-title">Daily Profit & Loss Spot Check</h1>

      <div className="avg-cogs-topbar">
        <button
          type="button"
          className="avg-cogs-btn"
          onClick={() =>
            navigate(`/dashboard/business/${businessId}/transactions-view`)
          }
        >
          Transactions
        </button>

        <button
          type="button"
          className="avg-cogs-btn"
          onClick={() =>
            navigate(
              `/dashboard/business/${businessId}/breakdown${
                accountId
                  ? `?account_id=${accountId}&mask=${accountMask || ""}&subtype=${
                      accountType || ""
                    }`
                  : ""
              }`
            )
          }
        >
          Breakdown
        </button>

        <button
          type="button"
          className="avg-cogs-btn"
          onClick={() => navigate(`/dashboard/business/${businessId}/fixed-costs`)}
        >
          Fixed Costs
        </button>

        <button
          type="button"
          className="avg-cogs-btn"
          onClick={fetchAverageCogs}
        >
          Refresh
        </button>
      </div>

      <div className="avg-cogs-pill">
        <div className="avg-cogs-account">
          Account: {accountType ? `${accountType} ${accountMask || ""}` : "All Accounts"}
        </div>
        <div className="avg-cogs-note">
          Based on income from Cash Entries and COST_OF_GOODS from Transactions for the last 3 months
        </div>
      </div>

      {loading ? (
        <div className="avg-cogs-loading">Loading...</div>
      ) : (
        <>
          <div className="avg-cogs-summary-grid">
            <div className="avg-cogs-card">
              <div className="avg-cogs-card-label">Total Income</div>
              <div className="avg-cogs-card-value">
                {formatMoney(data.totals.income)}
              </div>
            </div>

            <div className="avg-cogs-card">
              <div className="avg-cogs-card-label">Total Cost of Goods</div>
              <div className="avg-cogs-card-value">
                {formatMoney(data.totals.cogs)}
              </div>
            </div>

            <div className="avg-cogs-card avg-cogs-highlight">
              <div className="avg-cogs-card-label">Weighted Average COGS %</div>
              <div className="avg-cogs-card-value">
                {data.weighted_average_cogs_percentage}%
              </div>
            </div>

            <div className="avg-cogs-card">
              <div className="avg-cogs-card-label">Simple Average COGS %</div>
              <div className="avg-cogs-card-value">
                {data.simple_average_cogs_percentage}%
              </div>
            </div>
          </div>

          <div className="avg-cogs-table-wrap">
            <table className="avg-cogs-table">
              <thead>
                <tr>
                  <th>Month</th>
                  <th>Income</th>
                  <th>Cost of Goods</th>
                  <th>COGS %</th>
                </tr>
              </thead>
              <tbody>
                {data.months.length > 0 ? (
                  data.months.map((row) => (
                    <tr key={row.month}>
                      <td>{row.month}</td>
                      <td>{formatMoney(row.income)}</td>
                      <td>{formatMoney(row.cogs)}</td>
                      <td>{row.cogs_percentage}%</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="avg-cogs-empty">
                      No data found for the last 3 months.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="avg-cogs-calculation-box">
            <div className="avg-cogs-calculation-title">Use This Number For Calculations</div>
            <div className="avg-cogs-calculation-value">
              {data.weighted_average_cogs_percentage}%
            </div>
          </div>

          <div className="avg-cogs-input-card">
            <h2 className="pw-page-title">Daily Profit / Loss Spot Check</h2>

            <div className="avg-cogs-input-grid">
              <div className="avg-cogs-field">
                <label>Date</label>
                <input
                  type="date"
                  value={logDate}
                  onChange={(e) => setLogDate(e.target.value)}
                />
              </div>

              <div className="avg-cogs-field">
                <label>Net Sales</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={netSales}
                  onChange={(e) => setNetSales(e.target.value)}
                  placeholder="Enter net sales"
                />
              </div>

              <div className="avg-cogs-field readonly">
                <label>Calculated COGS</label>
                <input
                  type="text"
                  value={formatMoney(cogsAmount)}
                  readOnly
                />
              </div>

              <div className="avg-cogs-field readonly">
                <label>COGS % Used</label>
                <input
                  type="text"
                  value={`${data.weighted_average_cogs_percentage}%`}
                  readOnly
                />
              </div>
            </div>
          </div>

          <div className="avg-cogs-payroll-box">
            <div className="avg-cogs-payroll-header">
              <h2 className="pw-page-title">Daily Payroll Spot Check</h2>
              <button
                type="button"
                className="avg-cogs-add-btn"
                onClick={handleAddPayrollRow}
              >
                +
              </button>
            </div>

            {employeesLoading ? (
              <div className="avg-cogs-loading">Loading employee pay rates...</div>
            ) : (
              <>
                <div className="avg-cogs-payroll-table-wrap">
                  <table className="avg-cogs-payroll-table">
                    <thead>
                      <tr>
                        <th>Employee</th>
                        <th>Pay Type / Rate</th>
                        <th>Hours</th>
                        <th>Payroll Amount</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payrollRows.map((row, index) => {
                        const employee = getEmployeeById(row.employee_id);
                        const paymentType = String(employee?.payment_type || "").toLowerCase();
                        const payrollAmount = calculatePayrollAmount(row);

                        return (
                          <tr key={index}>
                            <td>
                              <select
                                value={row.employee_id}
                                onChange={(e) =>
                                  handlePayrollRowChange(index, "employee_id", e.target.value)
                                }
                              >
                                <option value="">Select Employee</option>
                                {employees.map((emp) => (
                                  <option key={emp.id} value={emp.id}>
                                    {emp.full_name}
                                  </option>
                                ))}
                              </select>
                            </td>

                            <td>{getEmployeeDisplayRate(employee)}</td>

                            <td>
                              {paymentType === "hourly" ? (
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={row.hours}
                                  onChange={(e) =>
                                    handlePayrollRowChange(index, "hours", e.target.value)
                                  }
                                  placeholder="Hours"
                                />
                              ) : paymentType === "salary" || paymentType === "salaried" ? (
                                <input type="text" value="1 Day" readOnly />
                              ) : (
                                <input type="text" value="" readOnly placeholder="—" />
                              )}
                            </td>

                            <td>{formatMoney(payrollAmount)}</td>

                            <td>
                              {payrollRows.length > 1 && (
                                <button
                                  type="button"
                                  className="avg-cogs-remove-btn"
                                  onClick={() => handleRemovePayrollRow(index)}
                                >
                                  Remove
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="avg-cogs-payroll-summary">
                  <div className="avg-cogs-payroll-total-card">
                    <div className="avg-cogs-card-label">Payroll Total</div>
                    <div className="avg-cogs-card-value">
                      {formatMoney(payrollSpotCheckTotal)}
                    </div>
                  </div>

                  <div className="avg-cogs-payroll-total-card">
                    <div className="avg-cogs-card-label">Payroll Taxes (7.65%)</div>
                    <div className="avg-cogs-card-value">
                      {formatMoney(payrollTaxAmount)}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="avg-cogs-fixed-box">
            <h2 className="pw-page-title">Fixed Costs Per Day</h2>

            {fixedCostsLoading ? (
              <div className="avg-cogs-loading">Loading fixed costs...</div>
            ) : (
              <>
                <div className="avg-cogs-fixed-grid">
                  <div className="avg-cogs-fixed-card">
                    <div className="avg-cogs-card-label">
                      Daily Share of Monthly Fixed Costs
                    </div>
                    <div className="avg-cogs-card-value">
                      {formatMoney(fixedDailyAmountTotal)}
                    </div>
                    <div className="avg-cogs-card-sub">
                      Monthly amounts are divided by {daysInMonth} days for the selected month.
                    </div>
                  </div>

                  <div className="avg-cogs-fixed-card">
                    <div className="avg-cogs-card-label">
                      Percentage Fixed Costs From Net Sales
                    </div>
                    <div className="avg-cogs-card-value">
                      {formatMoney(fixedPercentageAmountTotal)}
                    </div>
                    <div className="avg-cogs-card-sub">
                      Calculated only when Net Sales is entered.
                    </div>
                  </div>

                  <div className="avg-cogs-fixed-card avg-cogs-highlight">
                    <div className="avg-cogs-card-label">Total Fixed Costs</div>
                    <div className="avg-cogs-card-value">
                      {formatMoney(fixedCostTotal)}
                    </div>
                  </div>
                </div>

                <div className="avg-cogs-fixed-table-wrap">
                  <table className="avg-cogs-fixed-table">
                    <thead>
                      <tr>
                        <th>Cost Name</th>
                        <th>Type</th>
                        <th>Monthly Amount</th>
                        <th>Daily Amount</th>
                        <th>Percentage</th>
                        <th>Calculated Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fixedCosts.length > 0 ? (
                        fixedCosts.map((cost) => {
                          const amountRow = fixedAmountRows.find((r) => r.id === cost.id);
                          const percentageRow = fixedPercentageRows.find((r) => r.id === cost.id);

                          return (
                            <tr key={cost.id}>
                              <td>{cost.cost_name}</td>
                              <td>
                                {cost.cost_type === "amount"
                                  ? "Amount Per Month"
                                  : "Percentage Of Sales"}
                              </td>
                              <td>
                                {cost.cost_type === "amount"
                                  ? formatMoney(cost.amount)
                                  : "—"}
                              </td>
                              <td>
                                {cost.cost_type === "amount" && amountRow
                                  ? formatMoney(amountRow.daily_amount)
                                  : "—"}
                              </td>
                              <td>
                                {cost.cost_type === "percentage"
                                  ? `${toNum(cost.percentage).toFixed(2)}%`
                                  : "—"}
                              </td>
                              <td>
                                {cost.cost_type === "percentage" && percentageRow
                                  ? formatMoney(percentageRow.calculated_amount)
                                  : "—"}
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="6" className="avg-cogs-empty">
                            No fixed costs found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>

          <div className="avg-cogs-result-box">
            <h2 className="pw-page-title">Profit / Loss Result</h2>

            <div className="avg-cogs-result-grid">
              <div className="avg-cogs-result-row">
                <span>Net Sales</span>
                <strong>{formatMoney(numericNetSales)}</strong>
              </div>

              <div className="avg-cogs-result-row">
                <span>Less COGS</span>
                <strong>{formatMoney(cogsAmount)}</strong>
              </div>

              <div className="avg-cogs-result-row">
                <span>Less Payroll</span>
                <strong>{formatMoney(payrollSpotCheckTotal)}</strong>
              </div>

              <div className="avg-cogs-result-row">
                <span>Less Payroll Taxes (7.65%)</span>
                <strong>{formatMoney(payrollTaxAmount)}</strong>
              </div>

              <div className="avg-cogs-result-row">
                <span>Less Fixed Costs</span>
                <strong>{formatMoney(fixedCostTotal)}</strong>
              </div>

              <div className="avg-cogs-result-row total">
                <span>Total Profit / Loss</span>
                <strong>{formatMoney(totalProfitLoss)}</strong>
              </div>
            </div>

            <div className="avg-cogs-log-actions">
              <button
                type="button"
                className="avg-cogs-btn avg-cogs-log-btn"
                onClick={handleLogIt}
                disabled={logSaving}
              >
                {logSaving ? "Saving..." : "Log It"}
              </button>
            </div>
          </div>

          <div className="avg-cogs-logs-box">
            <h2 className="pw-page-title">Saved Daily Logs</h2>

            {logsLoading ? (
              <div className="avg-cogs-loading">Loading daily logs...</div>
            ) : logs.length === 0 ? (
              <div className="avg-cogs-empty">No logs saved yet.</div>
            ) : (
              <div className="avg-cogs-logs-table-wrap">
                <table className="avg-cogs-logs-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Net Sales</th>
                      <th>COGS</th>
                      <th>Payroll</th>
                      <th>Payroll Tax</th>
                      <th>Fixed Costs</th>
                      <th>Profit / Loss</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((row) => (
                      <tr key={row.id}>
                        <td>{new Date(row.log_date).toLocaleDateString()}</td>
                        <td>{formatMoney(row.net_sales)}</td>
                        <td>{formatMoney(row.cogs_amount)}</td>
                        <td>{formatMoney(row.payroll_amount)}</td>
                        <td>{formatMoney(row.payroll_tax_amount)}</td>
                        <td>{formatMoney(row.fixed_cost_total)}</td>
                        <td>{formatMoney(row.profit_loss_total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
    </div>
  );
};

export default AverageCogsPage;



// import React, { useEffect, useState } from "react";
// import { useLocation, useNavigate, useParams } from "react-router-dom";
// import api from "../services/api";
// import "./AverageCogsPage.css";

// const emptyPayrollRow = {
//   employee_id: "",
//   hours: "",
// };

// const AverageCogsPage = () => {
//   const { id: businessId } = useParams();
//   const location = useLocation();
//   const navigate = useNavigate();

//   const queryParams = new URLSearchParams(location.search);
//   const accountId = queryParams.get("account_id") || null;
//   const accountMask = queryParams.get("mask") || null;
//   const accountType = queryParams.get("subtype") || null;

//   const [loading, setLoading] = useState(true);
//   const [employeesLoading, setEmployeesLoading] = useState(true);

//   const [data, setData] = useState({
//     months: [],
//     totals: {
//       income: 0,
//       cogs: 0,
//     },
//     weighted_average_cogs_percentage: 0,
//     simple_average_cogs_percentage: 0,
//   });

//   const [employees, setEmployees] = useState([]);
//   const [payrollRows, setPayrollRows] = useState([{ ...emptyPayrollRow }]);

//   const formatMoney = (value) => {
//     const num = parseFloat(value || 0);
//     return Math.abs(num).toLocaleString("en-US", {
//       style: "currency",
//       currency: "USD",
//     });
//   };

//   const fetchAverageCogs = async () => {
//     try {
//       setLoading(true);

//       const params = {};
//       if (accountId) {
//         params.account_id = accountId;
//       }

//       const res = await api.get(`/reports/average-cogs/${businessId}`, {
//         params,
//       });

//       setData({
//         months: Array.isArray(res.data?.months) ? res.data.months : [],
//         totals: res.data?.totals || { income: 0, cogs: 0 },
//         weighted_average_cogs_percentage:
//           parseFloat(res.data?.weighted_average_cogs_percentage || 0),
//         simple_average_cogs_percentage:
//           parseFloat(res.data?.simple_average_cogs_percentage || 0),
//       });
//     } catch (err) {
//       console.error("Failed to fetch average COGS data:", err);
//       alert("Could not load Average Cost of Goods data.");
//     } finally {
//       setLoading(false);
//     }
//   };

//   const fetchEmployeePayrates = async () => {
//     try {
//       setEmployeesLoading(true);
//       const res = await api.get(
//         `/reports/average-cogs/${businessId}/employees-payrates`
//       );
//       setEmployees(Array.isArray(res.data) ? res.data : []);
//     } catch (err) {
//       console.error("Failed to fetch employee pay rates:", err);
//       alert("Could not load employee pay rates.");
//     } finally {
//       setEmployeesLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchAverageCogs();
//     fetchEmployeePayrates();
//     // eslint-disable-next-line
//   }, [businessId, accountId]);

//   const getEmployeeById = (employeeId) => {
//     return employees.find((emp) => String(emp.id) === String(employeeId)) || null;
//   };

//   const getEmployeeDisplayRate = (employee) => {
//     if (!employee) return "—";

//     const paymentType = String(employee.payment_type || "").toLowerCase();

//     if (paymentType === "hourly") {
//       return `${formatMoney(employee.hourly_amt)}/hr`;
//     }

//     if (paymentType === "salary" || paymentType === "salaried") {
//       return `${formatMoney(employee.salary_amt)}/yr (${formatMoney(employee.daily_rate)}/day)`;
//     }

//     return "—";
//   };

//   const calculatePayrollAmount = (row) => {
//     const employee = getEmployeeById(row.employee_id);
//     if (!employee) return 0;

//     const paymentType = String(employee.payment_type || "").toLowerCase();
//     const hours = parseFloat(row.hours || 0);

//     if (paymentType === "hourly") {
//       return Number(((employee.hourly_amt || 0) * hours).toFixed(2));
//     }

//     if (paymentType === "salary" || paymentType === "salaried") {
//       return Number(employee.daily_rate || 0);
//     }

//     return 0;
//   };

//   const handlePayrollRowChange = (index, field, value) => {
//     setPayrollRows((prev) =>
//       prev.map((row, i) =>
//         i === index
//           ? {
//               ...row,
//               [field]: value,
//             }
//           : row
//       )
//     );
//   };

//   const handleAddPayrollRow = () => {
//     setPayrollRows((prev) => [...prev, { ...emptyPayrollRow }]);
//   };

//   const handleRemovePayrollRow = (index) => {
//     setPayrollRows((prev) => prev.filter((_, i) => i !== index));
//   };

//   const payrollSpotCheckTotal = payrollRows.reduce(
//     (sum, row) => sum + calculatePayrollAmount(row),
//     0
//   );

//   const estimatedDailyProfit = data.totals.income - data.totals.cogs - payrollSpotCheckTotal;

//   return (
//     <div className="avg-cogs-container">
//       <h1 className="avg-cogs-title">Average Cost of Goods %</h1>

//       <div className="avg-cogs-topbar">
//         <button
//           type="button"
//           className="avg-cogs-btn"
//           onClick={() =>
//             navigate(`/dashboard/business/${businessId}/transactions-view`)
//           }
//         >
//           Transactions
//         </button>

//         <button
//           type="button"
//           className="avg-cogs-btn"
//           onClick={() =>
//             navigate(
//               `/dashboard/business/${businessId}/breakdown${
//                 accountId
//                   ? `?account_id=${accountId}&mask=${accountMask || ""}&subtype=${
//                       accountType || ""
//                     }`
//                   : ""
//               }`
//             )
//           }
//         >
//           Breakdown
//         </button>

//         <button
//         type="button"
//         className="avg-cogs-btn"
//         onClick={() => navigate(`/dashboard/business/${businessId}/fixed-costs`)}
//         >
//         Fixed Costs
//         </button>

//         <button
//           type="button"
//           className="avg-cogs-btn"
//           onClick={fetchAverageCogs}
//         >
//           Refresh
//         </button>
//       </div>

//       <div className="avg-cogs-pill">
//         <div className="avg-cogs-account">
//           Account: {accountType ? `${accountType} ${accountMask || ""}` : "All Accounts"}
//         </div>
//         <div className="avg-cogs-note">
//           Based on income from Cash Entries and COST_OF_GOODS from Transactions for the last 3 months
//         </div>
//       </div>

//       {loading ? (
//         <div className="avg-cogs-loading">Loading...</div>
//       ) : (
//         <>
//           <div className="avg-cogs-summary-grid">
//             <div className="avg-cogs-card">
//               <div className="avg-cogs-card-label">Total Income</div>
//               <div className="avg-cogs-card-value">
//                 {formatMoney(data.totals.income)}
//               </div>
//             </div>

//             <div className="avg-cogs-card">
//               <div className="avg-cogs-card-label">Total Cost of Goods</div>
//               <div className="avg-cogs-card-value">
//                 {formatMoney(data.totals.cogs)}
//               </div>
//             </div>

//             <div className="avg-cogs-card avg-cogs-highlight">
//               <div className="avg-cogs-card-label">Weighted Average COGS %</div>
//               <div className="avg-cogs-card-value">
//                 {data.weighted_average_cogs_percentage}%
//               </div>
//             </div>

//             <div className="avg-cogs-card">
//               <div className="avg-cogs-card-label">Simple Average COGS %</div>
//               <div className="avg-cogs-card-value">
//                 {data.simple_average_cogs_percentage}%
//               </div>
//             </div>
//           </div>

//           <div className="avg-cogs-table-wrap">
//             <table className="avg-cogs-table">
//               <thead>
//                 <tr>
//                   <th>Month</th>
//                   <th>Income</th>
//                   <th>Cost of Goods</th>
//                   <th>COGS %</th>
//                 </tr>
//               </thead>
//               <tbody>
//                 {data.months.length > 0 ? (
//                   data.months.map((row) => (
//                     <tr key={row.month}>
//                       <td>{row.month}</td>
//                       <td>{formatMoney(row.income)}</td>
//                       <td>{formatMoney(row.cogs)}</td>
//                       <td>{row.cogs_percentage}%</td>
//                     </tr>
//                   ))
//                 ) : (
//                   <tr>
//                     <td colSpan="4" className="avg-cogs-empty">
//                       No data found for the last 3 months.
//                     </td>
//                   </tr>
//                 )}
//               </tbody>
//             </table>
//           </div>

//           <div className="avg-cogs-calculation-box">
//             <div className="avg-cogs-calculation-title">Use This Number For Calculations</div>
//             <div className="avg-cogs-calculation-value">
//               {data.weighted_average_cogs_percentage}%
//             </div>
//           </div>

//           <div className="avg-cogs-payroll-box">
//             <div className="avg-cogs-payroll-header">
//               <h2>Daily Payroll Spot Check</h2>
//               <button
//                 type="button"
//                 className="avg-cogs-add-btn"
//                 onClick={handleAddPayrollRow}
//               >
//                 +
//               </button>
//             </div>

//             {employeesLoading ? (
//               <div className="avg-cogs-loading">Loading employee pay rates...</div>
//             ) : (
//               <>
//                 <div className="avg-cogs-payroll-table-wrap">
//                   <table className="avg-cogs-payroll-table">
//                     <thead>
//                       <tr>
//                         <th>Employee</th>
//                         <th>Pay Type / Rate</th>
//                         <th>Hours</th>
//                         <th>Payroll Amount</th>
//                         <th>Action</th>
//                       </tr>
//                     </thead>
//                     <tbody>
//                       {payrollRows.map((row, index) => {
//                         const employee = getEmployeeById(row.employee_id);
//                         const paymentType = String(employee?.payment_type || "").toLowerCase();
//                         const payrollAmount = calculatePayrollAmount(row);

//                         return (
//                           <tr key={index}>
//                             <td>
//                               <select
//                                 value={row.employee_id}
//                                 onChange={(e) =>
//                                   handlePayrollRowChange(index, "employee_id", e.target.value)
//                                 }
//                               >
//                                 <option value="">Select Employee</option>
//                                 {employees.map((emp) => (
//                                   <option key={emp.id} value={emp.id}>
//                                     {emp.full_name}
//                                   </option>
//                                 ))}
//                               </select>
//                             </td>

//                             <td>{getEmployeeDisplayRate(employee)}</td>

//                             <td>
//                               {paymentType === "hourly" ? (
//                                 <input
//                                   type="number"
//                                   min="0"
//                                   step="0.01"
//                                   value={row.hours}
//                                   onChange={(e) =>
//                                     handlePayrollRowChange(index, "hours", e.target.value)
//                                   }
//                                   placeholder="Hours"
//                                 />
//                               ) : paymentType === "salary" || paymentType === "salaried" ? (
//                                 <input
//                                   type="text"
//                                   value="1 Day"
//                                   readOnly
//                                 />
//                               ) : (
//                                 <input type="text" value="" readOnly placeholder="—" />
//                               )}
//                             </td>

//                             <td>{formatMoney(payrollAmount)}</td>

//                             <td>
//                               {payrollRows.length > 1 && (
//                                 <button
//                                   type="button"
//                                   className="avg-cogs-remove-btn"
//                                   onClick={() => handleRemovePayrollRow(index)}
//                                 >
//                                   Remove
//                                 </button>
//                               )}
//                             </td>
//                           </tr>
//                         );
//                       })}
//                     </tbody>
//                   </table>
//                 </div>

//                 <div className="avg-cogs-payroll-summary">
//                   <div className="avg-cogs-payroll-total-card">
//                     <div className="avg-cogs-card-label">Payroll Spot Check Total</div>
//                     <div className="avg-cogs-card-value">
//                       {formatMoney(payrollSpotCheckTotal)}
//                     </div>
//                   </div>

//                   <div className="avg-cogs-payroll-total-card avg-cogs-highlight">
//                     <div className="avg-cogs-card-label">Estimated Amount After COGS + Payroll</div>
//                     <div className="avg-cogs-card-value">
//                       {formatMoney(estimatedDailyProfit)}
//                     </div>
//                   </div>
//                 </div>
//               </>
//             )}
//           </div>
//         </>
//       )}
//     </div>
//   );
// };

// export default AverageCogsPage;


// import React, { useEffect, useState } from "react";
// import { useLocation, useNavigate, useParams } from "react-router-dom";
// import api from "../services/api";
// import "./AverageCogsPage.css";

// const AverageCogsPage = () => {
//   const { id: businessId } = useParams();
//   const location = useLocation();
//   const navigate = useNavigate();

//   const queryParams = new URLSearchParams(location.search);
//   const accountId = queryParams.get("account_id") || null;
//   const accountMask = queryParams.get("mask") || null;
//   const accountType = queryParams.get("subtype") || null;

//   const [loading, setLoading] = useState(true);
//   const [data, setData] = useState({
//     months: [],
//     totals: {
//       income: 0,
//       cogs: 0,
//     },
//     weighted_average_cogs_percentage: 0,
//     simple_average_cogs_percentage: 0,
//   });

//   const formatMoney = (value) => {
//     const num = parseFloat(value || 0);
//     return Math.abs(num).toLocaleString("en-US", {
//       style: "currency",
//       currency: "USD",
//     });
//   };

//   const fetchAverageCogs = async () => {
//     try {
//       setLoading(true);

//       const params = {};
//       if (accountId) {
//         params.account_id = accountId;
//       }

//       const res = await api.get(`/reports/average-cogs/${businessId}`, {
//         params,
//       });

//       setData({
//         months: Array.isArray(res.data?.months) ? res.data.months : [],
//         totals: res.data?.totals || { income: 0, cogs: 0 },
//         weighted_average_cogs_percentage:
//           parseFloat(res.data?.weighted_average_cogs_percentage || 0),
//         simple_average_cogs_percentage:
//           parseFloat(res.data?.simple_average_cogs_percentage || 0),
//       });
//     } catch (err) {
//       console.error("Failed to fetch average COGS data:", err);
//       alert("Could not load Average Cost of Goods data.");
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchAverageCogs();
//     // eslint-disable-next-line
//   }, [businessId, accountId]);

//   return (
//     <div className="avg-cogs-container">
//       <h1 className="avg-cogs-title">Average Cost of Goods %</h1>

//       <div className="avg-cogs-topbar">
//         <button
//           type="button"
//           className="avg-cogs-btn"
//           onClick={() =>
//             navigate(`/dashboard/business/${businessId}/transactions-view`)
//           }
//         >
//           Transactions
//         </button>

//         <button
//           type="button"
//           className="avg-cogs-btn"
//           onClick={() =>
//             navigate(
//               `/dashboard/business/${businessId}/breakdown${
//                 accountId
//                   ? `?account_id=${accountId}&mask=${accountMask || ""}&subtype=${
//                       accountType || ""
//                     }`
//                   : ""
//               }`
//             )
//           }
//         >
//           Breakdown
//         </button>

//         <button
//           type="button"
//           className="avg-cogs-btn"
//           onClick={fetchAverageCogs}
//         >
//           Refresh
//         </button>
//       </div>

//       <div className="avg-cogs-pill">
//         <div className="avg-cogs-account">
//           Account: {accountType ? `${accountType} ${accountMask || ""}` : "All Accounts"}
//         </div>
//         <div className="avg-cogs-note">
//           Based on income from Cash Entries and COST_OF_GOODS from Transactions for the last 3 months
//         </div>
//       </div>

//       {loading ? (
//         <div className="avg-cogs-loading">Loading...</div>
//       ) : (
//         <>
//           <div className="avg-cogs-summary-grid">
//             <div className="avg-cogs-card">
//               <div className="avg-cogs-card-label">Total Income</div>
//               <div className="avg-cogs-card-value">
//                 {formatMoney(data.totals.income)}
//               </div>
//             </div>

//             <div className="avg-cogs-card">
//               <div className="avg-cogs-card-label">Total Cost of Goods</div>
//               <div className="avg-cogs-card-value">
//                 {formatMoney(data.totals.cogs)}
//               </div>
//             </div>

//             <div className="avg-cogs-card avg-cogs-highlight">
//               <div className="avg-cogs-card-label">Weighted Average COGS %</div>
//               <div className="avg-cogs-card-value">
//                 {data.weighted_average_cogs_percentage}%
//               </div>
//             </div>

//             <div className="avg-cogs-card">
//               <div className="avg-cogs-card-label">Simple Average COGS %</div>
//               <div className="avg-cogs-card-value">
//                 {data.simple_average_cogs_percentage}%
//               </div>
//             </div>
//           </div>

//           <div className="avg-cogs-table-wrap">
//             <table className="avg-cogs-table">
//               <thead>
//                 <tr>
//                   <th>Month</th>
//                   <th>Income</th>
//                   <th>Cost of Goods</th>
//                   <th>COGS %</th>
//                 </tr>
//               </thead>
//               <tbody>
//                 {data.months.length > 0 ? (
//                   data.months.map((row) => (
//                     <tr key={row.month}>
//                       <td>{row.month}</td>
//                       <td>{formatMoney(row.income)}</td>
//                       <td>{formatMoney(row.cogs)}</td>
//                       <td>{row.cogs_percentage}%</td>
//                     </tr>
//                   ))
//                 ) : (
//                   <tr>
//                     <td colSpan="4" className="avg-cogs-empty">
//                       No data found for the last 3 months.
//                     </td>
//                   </tr>
//                 )}
//               </tbody>
//             </table>
//           </div>

//           <div className="avg-cogs-calculation-box">
//             <div className="avg-cogs-calculation-title">Use This Number For Calculations</div>
//             <div className="avg-cogs-calculation-value">
//               {data.weighted_average_cogs_percentage}%
//             </div>
//           </div>
//         </>
//       )}
//     </div>
//   );
// };

// export default AverageCogsPage;